import { useState } from 'react';
import { API_BASE_URL } from '../components/config/api';
import type { InvoiceCustomer } from '../types/order';

interface UseVatLookupReturn {
  vatNumber: string;
  setVatNumber: (value: string) => void;
  customer: InvoiceCustomer | null;
  setCustomer: (value: InvoiceCustomer | null) => void;
  isLoading: boolean;
  error: string | null;
  setError: (value: string | null) => void;
  /** vat: για καλούντα που κρατά το ΑΦΜ στη δική του φόρμα· αλλιώς το vatNumber του hook. */
  lookup: (storeId: number, vat?: string) => Promise<InvoiceCustomer | null>;
  reset: () => void;
}

/**
 * Αναζήτηση ΑΦΜ μέσω ΑΑΔΕ. Χρησιμοποιείται απευθείας από TakeAway, SelfCheckoutKiosk
 * και OrderPreviewModal (οθόνη Παραγγελιών / διόρθωση ειδών).
 */
export function useVatLookup(): UseVatLookupReturn {
  const [vatNumber, setVatNumber] = useState('');
  const [customer, setCustomer] = useState<InvoiceCustomer | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const lookup = async (storeId: number, vat: string = vatNumber): Promise<InvoiceCustomer | null> => {
    if (!vat || vat.length < 9) {
      setError('Please enter a valid VAT number (at least 9 digits)');
      return null;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/api/stores/${storeId}/aade/lookup/${vat}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}`
        }
      });

      const data = await response.json();

      if (response.ok && data.success) {
        const result: InvoiceCustomer = {
          vat_number: data.company.vat_number,
          name: data.company.name || data.company.commercial_title,
          country_code: data.company.country_code || 'GR',
          branch_code: data.company.branch_code || 0,
          address: {
            street: data.company.address?.street,
            number: data.company.address?.number,
            postal_code: data.company.address?.postal_code,
            city: data.company.address?.city
          }
        };
        setCustomer(result);
        setError(null);
        return result;
      } else {
        setError(data.error || 'Company not found');
        setCustomer(null);
        return null;
      }
    } catch (err) {
      console.error('VAT lookup error:', err);
      setError('Failed to lookup VAT number. Please check your TAXISnet credentials in ECR Settings.');
      setCustomer(null);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const reset = () => {
    setVatNumber('');
    setCustomer(null);
    setError(null);
  };

  return {
    vatNumber,
    setVatNumber,
    customer,
    setCustomer,
    isLoading,
    error,
    setError,
    lookup,
    reset
  };
}
